import { Fragment, ReactNode } from 'react'

import { getImageUrl } from '~/components/Image/helpers'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import supportsAlphaChannel from '~/utils/supportsAlphaChannel'
import { IconName } from '~/components/Icons'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import CheckmarkIcon from '~/components/Icons/svgs/CheckmarkIcon'
import HomGraduationCapCircleIcon from '~/components/Icons/svgs/HomGraduationCapCircleIcon'
import HomCameraCircleIcon from '~/components/Icons/svgs/HomCameraCircleIcon'
import HomChatCircleIcon from '~/components/Icons/svgs/HomChatCircleIcon'

import { getClassNames } from './ProductCard.utils'

export default function ProductCardSlider({
  button,
  colorScheme = 'purple',
  highlights,
  icon,
  price,
  title,
  video,
  videoCx,
  videoFallback,
  videoFallbackCx,
  videoFallbackHeight,
  videoFallbackWidth,
}: {
  button: ReactNode
  colorScheme?: 'sky' | 'white' | 'purple'
  highlights: string[]
  icon: IconName
  price: string
  title: string
  video: string
  videoCx?: string
  videoFallback: string
  videoFallbackCx?: string
  videoFallbackHeight: number
  videoFallbackWidth: number
}): React.ReactElement {
  const { bg, checkmarkColor } = getClassNames(colorScheme)
  const isBigScreen = useCheckBreakpoint({ gt: Breakpoint.XL })
  const mediaQuery = [{ screen: '(min-width: 280px)', width: `${videoFallbackWidth}px` }]

  const getIconComponent = (name: string): ((props: { [key: string]: any }) => React.ReactElement) => {
    switch (name) {
      case 'hom-graduation-cap-circle':
        return HomGraduationCapCircleIcon
      case 'hom-camera-circle':
        return HomCameraCircleIcon
      case 'hom-chat-circle':
        return HomChatCircleIcon
      default:
        return HomChatCircleIcon
    }
  }

  return (
    <div
      className={`sm:-mx-10 lg:-mx-16 xl:-mx-20 h-full relative rounded ${bg} shadow-card mx-auto w-11/12 sm:w-auto max-w-[340px] sm:max-w-[450px] xl:max-w-[570px]`}
    >
      <span className="absolute z-20 top-0 left-1/2 -translate-y-1/2 -translate-x-1/2 drop-shadow-lg">
        <SVGIcon icon={getIconComponent(icon)} size={isBigScreen ? 90 : 70} />
      </span>
      <section className="mt-4 p-8 space-y-6 text-black-light">
        <h3 className="pt-3 text-center text-lg xl:text-h3 font-semibold sm:h-128">{title}</h3>
        <ul className="space-y-1.5 sm:h-56 lg:h-36 xl:h-60 xl:text-lg">
          {highlights.map((elem) => (
            <li className="space-x-4 flex justify-between" key={elem}>
              <span>{elem}</span>
              <span className="shrink-0">
                <SVGIcon icon={CheckmarkIcon} pathClassName={checkmarkColor} size={20} />
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-center flex-col justify-center space-y-5">
          <p className="text-2xl sm:text-xl lg:text-3xl font-semibold">{price}</p>
          <Fragment>{button}</Fragment>
        </div>
      </section>
      <div className="mt-auto rounded overflow-hidden">
        {supportsAlphaChannel() ? (
          <video
            autoPlay
            className={`mx-auto ${videoCx} h-[300px]`}
            loop
            muted
            playsInline
            poster={
              getImageUrl(videoFallbackHeight, videoFallbackWidth, 'cover', videoFallback, 'hom-frontend-assets')
                .urlWithFormat
            }
          >
            <source src={`${video}.mp4`} type="video/mp4;codecs=hvc1" />
            <source src={`${video}.webm`} type="video/webm" />
          </video>
        ) : (
          <ResponsiveImage
            alt={title}
            height={videoFallbackHeight}
            imageClassName={`mx-auto ${videoFallbackCx}`}
            imageWidths={`${videoFallbackWidth}`}
            mediaQuery={mediaQuery}
            srcKey={videoFallback}
            width={videoFallbackWidth}
          />
        )}
      </div>
    </div>
  )
}
